import { useCallback, useEffect, useRef } from "react";

import { useListContext, useNotify } from "react-admin";

/** Shown when a mixed-account selection is trimmed back to one account. */
export const MIXED_ACCOUNT_MESSAGE =
  "Only loads from one account can ship together";

/**
 * The account the selection is locked to: the account of the first selected
 * row that is on this page, or null when nothing is selected.
 */
export const getSelectionAccountId = (data, selectedIds) => {
  const row = (selectedIds || []).map((id) => data?.[id]).find(Boolean);
  return row?.accountId ?? null;
};

/**
 * The selection with the rows of another account dropped. Ids whose row is not
 * on this page are kept — they cannot be judged here, and the Ships Together
 * button already refuses a selection it cannot see in full.
 */
export const sameAccountIds = (data, selectedIds, accountId) =>
  (selectedIds || []).filter((id) => {
    const row = data?.[id];
    return !row || row.accountId === accountId;
  });

/**
 * Keeps a ships-together selection inside one account (AD-8): once a load is
 * picked, rows of another account lose their checkbox, and a select-all that
 * crossed accounts is trimmed back to the first account with a notice. Linking
 * across accounts is refused by the button and by the lambda anyway; this stops
 * the user from building the invalid selection in the first place.
 *
 * The lock survives paging: the list context holds only the current page's
 * rows, so once the user leaves the page the selection was made on, the account
 * is remembered in a ref until the selection is cleared. (A selection spanning
 * pages cannot be linked anyway — the button refuses what it cannot see — but
 * the checkboxes stay consistent with what was already picked.)
 *
 * Returns the Datagrid `isRowSelectable` predicate, or undefined on tabs where
 * `enabled` is false, so every row there stays selectable.
 */
export default function useShipWithSelection(enabled) {
  const { data, selectedIds, onSelect } = useListContext();
  const notify = useNotify();
  // Cache, not state: it only ever repeats what this render already knows, so
  // updating it during render cannot show a stale page of checkboxes.
  const anchorAccountIdRef = useRef(null);
  const pageAccountId = enabled
    ? getSelectionAccountId(data, selectedIds)
    : null;
  if (!enabled || !(selectedIds || []).length) anchorAccountIdRef.current = null;
  else if (pageAccountId) anchorAccountIdRef.current = pageAccountId;
  const lockedAccountId = anchorAccountIdRef.current;

  useEffect(() => {
    if (!lockedAccountId) return;
    const kept = sameAccountIds(data, selectedIds, lockedAccountId);
    if (kept.length === (selectedIds || []).length) return;
    notify(MIXED_ACCOUNT_MESSAGE, "warning");
    onSelect(kept);
  }, [data, selectedIds, lockedAccountId, notify, onSelect]);

  const isRowSelectable = useCallback(
    (record) => !lockedAccountId || record?.accountId === lockedAccountId,
    [lockedAccountId],
  );

  return enabled ? isRowSelectable : undefined;
}
